<template>
  <div class="dual-switch-container">
    <template v-if="$slots.default">
      <label class="label">
        <span class="is-semi-bold font-sm">
          <slot />
        </span>
      </label>
    </template>

    <section
      :class="{
        'has-value': !!modelValue,
        'is-featured': activeOption?.featured,
      }"
      class="multi-switch"
      :style="thumbStyle"
    >
      <span
        v-for="(opt, index) in options"
        :key="index"
        class="multi-switch__item"
        :class="{
          'is-active': modelValue === opt.value,
          'is-featured': opt.featured,
        }"
        @click="$emit('change', opt.value)"
      >
        {{ opt.label || opt.name }}
        <ChartArrowIcon v-if="opt.featured" class="multi-switch__icon" />
      </span>
    </section>

    <div class="footer-message">
      <small
        v-if="errorMessage"
        class="has-text-danger"
      >
        {{ errorMessage }}
      </small>
      <small
        v-else-if="activeOption?.featured && activeOption.message"
        class="featured-message"
      >
        {{ activeOption.message }}
      </small>
    </div>
  </div>
</template>

<script>
import ChartArrowIcon from '@/assets/icons/ChartArrow.vue';

export default {
  name: 'DualSwitch',
  components: { ChartArrowIcon },
  model: {
    prop: 'modelValue',
    event: 'change',
  },
  props: {
    options: {
      type: Array,
      required: true,
      validator: (opts) => opts.length >= 2 && opts.length <= 5,
    },
    modelValue: {
      type: [String, Number, Boolean],
      required: true,
    },
    errorMessage: {
      type: String,
      default: '',
      required: false,
    },
  },
  emits: ['change'],
  computed: {
    activeIndex() {
      return this.options.findIndex((opt) => opt.value === this.modelValue);
    },
    activeOption() {
      return this.options[this.activeIndex] || null;
    },
    thumbStyle() {
      return {
        '--thumb-count': this.options.length,
        '--thumb-offset': this.activeIndex,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/scss/src/_textVariables.scss';

.label {
  margin-bottom: 4px;

  span {
    font-family: Inter;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    color: #666;
    display: flex;
    align-items: center;
    gap: 4px;
  }
}

.footer-message {
  margin-top: 4px;

  height: 20px;
  display: flex;
  align-items: center;

  .featured-message {
    margin-left: .5rem;
    font: {
      family: 'Inter', sans-serif;
      size: 12px;
      weight: 500;
    }
    text-transform: lowercase;
    color: #5B21B6;
  }
}

.multi-switch {
  position: relative;
  display: flex;
  width: 100%;
  height: 42px;
  padding: 2px;
  border-radius: 6px;
  gap: 2px;
  background-color: $slate-200;

  &.has-value {
    &::before {
      content: '';

      position: absolute;
      top: 2px;
      left: 2px;

      width: calc((100% - 4px) / var(--thumb-count));
      height: calc(100% - 4px);
      background: #fff;
      border-radius: 6px;
      border: 2px solid #fff;
      box-shadow: 0px 1px 2px 0px #0000000d;
      will-change: transform;
      transition: transform 0.25s ease-in-out, background-color 0.2s ease;
      transform: translateX(calc(var(--thumb-offset) * 100%));
    }

    &.is-featured {
      &::before {
        border-color: #DDD6FE;
      }
    }
  }

  &__item {
    flex: 1;

    position: relative;
    margin: auto;

    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    height: 100%;
    font: 400 15px 'Inter';
    color: #4B5563;
    text-align: center;

    cursor: pointer;
    user-select: none;
    z-index: 1;

    transition: color 0.25s cubic-bezier(0.45, 0.05, 0.55, 0.95);
    user-select: none;

    &.is-active {
      font-weight: 700;
      color: #1F2937;

      &.is-featured {
        color: #5B21B6;
      }
    }
  }

  &__icon {
    $size: 1rem;

    width: $size;
    height: $size;
    color: #7C3AED;
    flex-shrink: 0;

    .is-active.is-featured & {
      color: #5B21B6;
    }
  }
}
</style>
